import React, { useState } from 'react'
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Alert,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import LockIcon from '@mui/icons-material/Lock'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import VenafiCertificatePreview from './VenafiCertificatePreview'

/**
 * Parses a Venafi certificate DN into human-readable segments.
 * e.g. "\\VED\\Policy\\Production\\BRCC DigiCert...\\Automation\\DEP\\app.broadridge.com"
 *      → { env: 'Production', ca: 'BRCC DigiCert Standard CA', folder: 'DEP', cn: 'app.broadridge.com' }
 */
const parseDN = (dn = '') => {
  const parts = dn.split('\\').filter(Boolean)
  // Remove leading VED/Policy
  const meaningful = parts.filter(p => !['VED', 'Policy'].includes(p))

  const envPart = meaningful.find(p =>
    p.toLowerCase().includes('production') || p.toLowerCase().includes('non-production')
  )
  const automation = meaningful.indexOf('Automation')
  const afterAuto = automation !== -1 ? meaningful.slice(automation + 1) : []
  const cn = afterAuto[afterAuto.length - 1] || meaningful[meaningful.length - 1] || dn
  const folder = afterAuto.length > 1 ? afterAuto.slice(0, -1).join(' › ') : null

  const caPart = meaningful.find(p =>
    p.toLowerCase().includes('digicert') || p.toLowerCase().includes('entrust') || p.toLowerCase().includes(' ca')
  )

  const isProd =
    envPart?.toLowerCase().includes('production') &&
    !envPart?.toLowerCase().includes('non-production')

  return { env: envPart || null, isProd, ca: caPart || null, folder, cn }
}

/**
 * Single certificate selection card shown in the list.
 */
const CertCard = ({ match, index, isSelected, onSelect, isLoading }) => {
  const [expanded, setExpanded] = useState(false)
  const parsed = parseDN(match.certificate_dn || match.policy_dn || '')
  const totalCount = match.sans?.length || 0

  return (
    <Paper
      elevation={isSelected ? 4 : 1}
      sx={{
        border: '2px solid',
        borderColor: isSelected ? 'success.main' : 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: isSelected ? 'success.main' : 'primary.main',
          boxShadow: 4,
        },
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          background: isSelected
            ? 'linear-gradient(90deg, #1b5e20 0%, #2e7d32 100%)'
            : parsed.isProd
            ? 'linear-gradient(90deg, #1a2c50 0%, #243858 100%)'
            : 'linear-gradient(90deg, #1a3050 0%, #1f3a60 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <LockIcon sx={{ color: isSelected ? '#a5d6a7' : '#90caf9', fontSize: 18, flexShrink: 0 }} />
        <Typography
          sx={{
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.85rem',
            flex: 1,
            fontFamily: '"Roboto Mono","Courier New",monospace',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {parsed.cn}
        </Typography>
        <Chip
          label={parsed.isProd ? 'PRD' : 'NON-PRD'}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.6rem',
            fontWeight: 700,
            bgcolor: parsed.isProd ? 'error.dark' : 'info.dark',
            color: '#fff',
            flexShrink: 0,
          }}
        />
        {isSelected && (
          <CheckCircleIcon sx={{ color: '#a5d6a7', fontSize: 18, flexShrink: 0 }} />
        )}
      </Box>

      {/* Card body */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Grid container spacing={1.5}>
          {parsed.ca && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                Certificate Authority
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                {parsed.ca}
              </Typography>
            </Grid>
          )}
          {parsed.folder && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                Folder Path
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FolderOpenIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: '"Roboto Mono","Courier New",monospace' }}>
                  {parsed.folder}
                </Typography>
              </Box>
            </Grid>
          )}
          {match.guid && (
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                GUID
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontFamily: '"Roboto Mono","Courier New",monospace', color: 'text.secondary', fontSize: '0.7rem' }}
              >
                {match.guid}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* SANs expandable */}
        {totalCount > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setExpanded(e => !e)}
            >
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, flex: 1 }}>
                {totalCount} Subject Alternative Name{totalCount !== 1 ? 's' : ''}
              </Typography>
              <IconButton size="small" sx={{ p: 0 }}>
                {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={expanded}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                {match.sans.map((san, i) => (
                  <Chip
                    key={i}
                    label={san}
                    size="small"
                    sx={{ fontFamily: '"Roboto Mono","Courier New",monospace', fontSize: '0.65rem', height: 20 }}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      {/* Card footer / action */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'grey.100',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant={isSelected ? 'contained' : 'outlined'}
          color={isSelected ? 'success' : 'primary'}
          size="small"
          disabled={isLoading}
          onClick={() => !isSelected && onSelect(match)}
          startIcon={
            isLoading && isSelected
              ? <CircularProgress size={14} color="inherit" />
              : isLoading && !isSelected
              ? <CircularProgress size={14} color="inherit" />
              : null
          }
          sx={{ minWidth: 110 }}
        >
          {isSelected && isLoading
            ? 'Fetching details…'
            : isSelected
            ? 'Selected ✓'
            : isLoading
            ? 'Loading…'
            : 'Select'
          }
        </Button>
      </Box>
    </Paper>
  )
}

const PreExistingCertFields = ({
  formData,
  setFormData,
  venafiLookup,
  selectVenafiMatch,
}) => {
  const [showAllCerts, setShowAllCerts] = useState(false)
  const isLoading = venafiLookup.status === 'loading'
  const hasSelection = !!venafiLookup.selectedMatch
  const matches = venafiLookup.matches || []

  // How many to show collapsed
  const COLLAPSED_LIMIT = 3
  const visibleMatches = showAllCerts ? matches : matches.slice(0, COLLAPSED_LIMIT)

  return (
    <>
      {/* ── Searching spinner (CN lookup, no selection yet) ─────────────── */}
      {venafiLookup.status === 'loading' && !hasSelection && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Searching Venafi for matching certificates…
            </Typography>
          </Box>
        </Grid>
      )}

      {/* ── Detail-fetch loading (cert selected, waiting for API) ────────── */}
      {venafiLookup.status === 'loading' && hasSelection && (
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Retrieving certificate details from Venafi…
            </Typography>
          </Box>
        </Grid>
      )}

      {/* ── Error ───────────────────────────────────── */}
      {venafiLookup.status === 'error' && (
        <Grid item xs={12}>
          <Alert severity="error">{venafiLookup.error}</Alert>
        </Grid>
      )}

      {/* ── No results ──────────────────────────────── */}
      {venafiLookup.status === 'success' && !venafiLookup.exists && (
        <Grid item xs={12}>
          <Alert severity="success" sx={{ alignItems: 'center' }}>
            No existing certificate found in Venafi for this common name. Venafi Path and SANs can be filled in the next steps.
          </Alert>
        </Grid>
      )}

      {/* ── Results found ───────────────────────────── */}
      {(venafiLookup.status === 'success' || (venafiLookup.status === 'loading' && hasSelection)) && venafiLookup.exists && (
        <Grid item xs={12}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {matches.length} certificate{matches.length !== 1 ? 's' : ''} found in Venafi
            </Typography>
            {venafiLookup.truncated && (
              <Chip label="results truncated" size="small" color="warning" variant="outlined" sx={{ ml: 1, fontSize: '0.65rem', height: 20 }} />
            )}
          </Box>

          {/* ── Card grid ───────────────────────────── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {visibleMatches.map((match, index) => (
              <CertCard
                key={`${match.certificate_dn || match.guid || 'match'}-${index}`}
                match={match}
                index={index}
                isSelected={
                  hasSelection &&
                  (venafiLookup.selectedMatch.guid === match.guid ||
                    venafiLookup.selectedMatch.certificate_dn === match.certificate_dn)
                }
                onSelect={selectVenafiMatch}
                isLoading={isLoading}
              />
            ))}
          </Box>

          {/* Show more / less toggle */}
          {matches.length > COLLAPSED_LIMIT && (
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAllCerts(v => !v)}
              sx={{ mt: 1, color: 'text.secondary' }}
              startIcon={showAllCerts ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            >
              {showAllCerts
                ? 'Show fewer'
                : `Show ${matches.length - COLLAPSED_LIMIT} more certificate${matches.length - COLLAPSED_LIMIT !== 1 ? 's' : ''}`}
            </Button>
          )}

          {/* ── Certificate preview (below cards) — shown after selection ── */}
          {hasSelection && (
            <Box sx={{ mt: 2 }}>
              <VenafiCertificatePreview
                selectedMatch={venafiLookup.selectedMatch}
                formData={formData}
              />
            </Box>
          )}
        </Grid>
      )}

      {/* Certificate Usage + Client Name — only once a cert is selected */}
      {hasSelection && (
        <>
          {/* Certificate Usage - auto-populated */}
          <Grid item xs={12}>
            <FormControl fullWidth required disabled>
              <InputLabel>Certificate Usage *</InputLabel>
              <Select
                value={formData.certificateUsage}
                label="Certificate Usage *"
              >
                <MenuItem value="server-internal">Server Cert - Internal</MenuItem>
                <MenuItem value="server-public">Server Cert - Public/Internet-Facing</MenuItem>
                <MenuItem value="client-internal">Client Cert - Internal</MenuItem>
                <MenuItem value="client-broadridge">Client Cert - Broadridge-Managed for External Use</MenuItem>
                <MenuItem value="client-external">Client Cert - Externally-Managed</MenuItem>
                <MenuItem value="other">Other - please explain in notes</MenuItem>
              </Select>
              <FormHelperText>Auto-populated from Venafi certificate</FormHelperText>
            </FormControl>
          </Grid>

          {/* Client Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Client Name"
              value={formData.clientName || 'N/A'}
              disabled
              InputProps={{ readOnly: true }}
              helperText="Auto-populated from Venafi certificate"
            />
          </Grid>
        </>
      )}
    </>
  )
}

export default PreExistingCertFields

